import { Component, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ToolsService } from 'src/app/service/tools/tools.service';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { ToolTransferService } from '../tool-transfer/tool-transfer.service';
import { ToolHistoryData, ToolHistoryDetail, ToolHistoryRecord } from '../shared/types/tool-history-record';
import { LoaderService } from 'src/app/service/shared/loader/loader.service';
import { MomentService } from 'src/app/service/shared/moment/moment.service';

type ToolHistoryChangedType = {
  field: string;
  value: number | string;
  previousValue: number | string;
  valueLabel: string;
  previousValueLabel: string;
};

@Component({
  selector: 'app-tool-history',
  templateUrl: './tool-history.component.html',
  styleUrls: ['./tool-history.component.scss'],
})
export class ToolHistoryComponent implements OnInit {
  Object = Object;
  snapshot;
  snapshotDates;

  id = null;
  search = '';
  params = {};

  types = [
    {
      label: 'Add',
      value: 'add',
    },
    {
      label: 'Edit',
      value: 'edit',
    },
    {
      label: 'Duplicate',
      value: 'duplicate',
    },
    {
      label: 'Assignment',
      value: 'assignment',
    },
    {
      label: 'Inventory',
      value: 'inventory',
    },
  ];

  constructor(
    private activatedRoute: ActivatedRoute,
    public toolsService: ToolsService,
    public toolTransferService: ToolTransferService,
    protected loaderService: LoaderService,
    protected momentService: MomentService,
  ) {}

  refreshSubscription: Subscription;

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
      this.id = +params.get('id');
      this.fetch(this.id);
    });

    //Refresh tool history after transfer
    this.refreshSubscription = this.toolTransferService.refreshToolsSubscriber.subscribe(res => {
      if (res.next?.length) {
        this.fetch(this.id);
      }
    });
  }

  ngOnDestroy() {
    if (this.refreshSubscription) this.refreshSubscription.unsubscribe();
  }

  handleSearch() {
    this.params = {
      ...this.params,
      search: this.search,
      fields: 'type,summary,detail,data',
      page: 1,
    };
    this.fetch(this.id, this.params);
  }

  filterByDate(event: any) {
    this.params = { ...this.params, date: event.target.value, page: 1 };
    this.fetch(this.id, this.params);
  }

  filterByType(event: any) {
    this.params = {
      ...this.params,
      search: event.target.value,
      fields: 'type',
      page: 1,
    };
    this.fetch(this.id, this.params);
  }

  fetch(id: number, params: any = {}) {
    this.loaderService.displayLoader();
    const options = { ...params, requireAdditionalFields: true };

    this.toolsService.history.tools(id, options).subscribe(
      res => {
        const { data, pagination } = res;

        this.toolsService.readById
          .tools(id, {
            cache: 'clear',
            embed: 'assignment,model,vehicleDetail',
          })
          .subscribe(res => {
            const { data: tool } = res;

            this.snapshot = { data, pagination, tool };

            this.snapshotDates = { id, data: Object.keys(data) };
            this.loaderService.hideLoader();
          });
      },
      err => {
        console.error(err);
        this.loaderService.hideLoader();
      },
    );
  }

  fetchNext(nextPage: number, event: any) {
    event.preventDefault();
    this.params = { ...this.params, page: nextPage };
    this.toolsService.history.tools(this.id, this.params).subscribe(res => {
      const { data, pagination } = res;
      this.mergeData({ data, pagination });
    });
  }

  mergeData(obj: any) {
    // get the new data
    const { data: newData } = obj;
    // get the current data
    const currentData = this.snapshot.data;

    // iterate over new data keys
    Object.keys(newData).forEach(key => {
      // add new data to merged data
      if (!currentData[key]) currentData[key] = [];
      currentData[key] = [...currentData[key], ...newData[key]];
    });

    this.snapshot.data = currentData;
    this.snapshot.pagination = obj.pagination;
  }

  getDetail(row: ToolHistoryRecord): ToolHistoryDetail {
    if (typeof row.detail !== 'string') return row.detail;

    return JSON.parse(row.detail);
  }

  getData(row: ToolHistoryRecord): ToolHistoryData {
    if (typeof row.data !== 'string') return row.data;

    return JSON.parse(row.data);
  }

  getDate(row: ToolHistoryRecord): string {
    const detail = this.getDetail(row);
    return this.momentService.getGeneralDateFormat(detail.date, 'MM/DD/YYYY, hh:mm A');
  }

  getDuplicate(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return detail.duplicate;
  }

  hasUser(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return detail.user;
  }

  getUserName(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return `${detail.user.firstName} ${detail.user.lastName}`;
  }

  getToolAlertMessage(row: ToolHistoryRecord) {
    const data = this.getData(row);
    return data.message;
  }

  getImages(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return detail.images.slice(0, 3);
  }

  getRemainingImages(row: ToolHistoryRecord) {
    const currentImages = this.getImages(row);
    const totalImages = this.getDetail(row).images;
    return totalImages.length - currentImages.length;
  }

  hasImages(row: ToolHistoryRecord): boolean {
    const detail = this.getDetail(row);
    return !!detail?.images?.length;
  }

  getImage(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return detail.image;
  }

  hasDocuments(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return !!detail?.documents?.length;
  }

  getDocuments(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return detail.documents;
  }

  getChanges(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return detail.changes;
  }

  hasChanges(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return !!detail?.changes?.length;
  }

  getAssignedFrom(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    const append = detail?.assignedFrom?.append;
    return this.getAssigned(append);
  }

  getAssignedTo(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    const append = detail?.assignedTo?.append;
    return this.getAssigned(append);
  }

  getAssigned(append) {
    if (append?.job) {
      return append.job.jobNumber ? `${append.job.jobNumber} - ${append.job.name}` : append.job.name;
    }
    if (append?.owner) {
      return append.owner.name;
    }
    if (append?.shop) {
      return append.shop.name;
    }
    if (append?.vendor) {
      return append.vendor.name;
    }
    if (append?.employee) {
      return append.employee.name;
    }
    if (append?.type) {
      return append.type.name;
    }
  }

  getDaysHad(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return detail?.daysHad?.days;
  }

  getNotes(row: ToolHistoryRecord) {
    const detail = this.getDetail(row);
    return detail?.notes ?? '';
  }

  getBatchId(row: ToolHistoryRecord): ToolHistoryData['batchId'] {
    return this.getData(row)?.batchId;
  }

  getInventoryBatchNumber(row: ToolHistoryRecord) {
    return this.getChanges(row).find(el => el.field === 'inventoryBatchNumber')?.value;
  }

  getInventoriedDate(row: ToolHistoryRecord) {
    const details = this.getDetail(row);
    return this.momentService.getGeneralDateFormat(details?.inventory?.inventoriedDate, 'MM/DD/YYYY, hh:mm A');
  }

  getInventoriedNotes(row: ToolHistoryRecord) {
    const details = this.getDetail(row);
    return details?.inventory?.notes ?? '';
  }

  // Expected return: "2025-02-03T20:41:07.037+00:00"
  getAssignedDate(row: ToolHistoryRecord): string {
    const detail = this.getDetail(row);
    return detail?.assignedTo?.assignedDate ?? '';
  }

  getChangedFromLabel(change: ToolHistoryChangedType) {
    const hasLabel = !!change.previousValueLabel;
    return hasLabel ? change.previousValueLabel : change.previousValue;
  }

  getChangedToLabel(change: ToolHistoryChangedType) {
    const hasLabel = !!change.valueLabel;
    return hasLabel ? change.valueLabel : change.value;
  }
}
